import React, { useState, useRef } from "react";
import Barcode from "react-barcode";
import type { BasketItem } from "../page";
import { isSunmiDevice, printOnSunmi } from "../../../utils/sunmiPrinter";
import { toast } from "../../../components/toast";

interface ReturnReceiptModalProps {
  onClose: () => void;
  basketItems: BasketItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  isRefund: boolean;
  creditMemoAmount?: number;
  receiptData?: {
    receiptNo?: string;
    creditMemoNumber?: string;
    saleDate?: string;
    cashier?: string;
    customerName?: string;
    customerCode?: string | null;
    binNumber?: string | null;
  };
}

export default function ReturnReceiptModal({
  onClose,
  basketItems,
  subtotal,
  discount,
  tax,
  isRefund,
  creditMemoAmount,
  receiptData,
}: ReturnReceiptModalProps) {
  const [isPrinting, setIsPrinting] = useState(false);
  const receiptPreviewRef = useRef<HTMLDivElement>(null);

  const printerConnected =
    isSunmiDevice() && (window.SunmiPrinter?.isConnected() ?? false);

  const toRateNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseDateSafe = (value: unknown): Date | null => {
    if (!value) return null;
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getActiveItemDiscount = (item: BasketItem) => {
    const now = new Date();
    const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
    return (
      discounts.find((d) => {
        const from = parseDateSafe(
          d?.effectiveFrom ?? d?.effectiveFromDate ?? d?.startDate ?? d?.fromDate ?? d?.validFrom,
        );
        const to = parseDateSafe(
          d?.effectiveTo ?? d?.effectiveToDate ?? d?.endDate ?? d?.toDate ?? d?.validTo,
        );
        if (from && now < from) return false;
        if (to && now > to) return false;
        return true;
      }) ?? null
    );
  };

  const getItemDiscountAmount = (item: BasketItem): number => {
    const activeDiscount = getActiveItemDiscount(item);
    if (!activeDiscount) return 0;
    const lineBase = Number(item.price || 0) * Number(item.qty || 0);
    if (lineBase <= 0) return 0;
    const typeName = String(
      activeDiscount?.discountType?.name ??
        activeDiscount?.discountTypeName ??
        activeDiscount?.amountType?.name ??
        activeDiscount?.amountTypeName ??
        activeDiscount?.type ??
        activeDiscount?.valueType ??
        "",
    ).toLowerCase();
    const typeId = Number(
      activeDiscount?.discountType?.id ??
        activeDiscount?.discountTypeId ??
        activeDiscount?.amountType?.id ??
        activeDiscount?.amountTypeId ??
        -1,
    );
    const isPercentage =
      activeDiscount?.isPercentage === true ||
      typeId === 1 ||
      typeName.includes("percent");
    const discountValue = toRateNumber(
      activeDiscount?.percentage ??
        activeDiscount?.percent ??
        activeDiscount?.rate ??
        activeDiscount?.discountPercentage ??
        activeDiscount?.amount ??
        activeDiscount?.discountAmount ??
        activeDiscount?.value,
    );
    if (discountValue <= 0) return 0;
    let discountAmount = isPercentage
      ? lineBase * (discountValue / 100)
      : discountValue * Number(item.qty || 0);
    const maxValue = toRateNumber(
      activeDiscount?.maximumValue ??
        activeDiscount?.maximumDiscount ??
        activeDiscount?.maxDiscount ??
        activeDiscount?.maxValue ??
        activeDiscount?.maxAmount ??
        activeDiscount?.maximumAmount,
    );
    if (maxValue > 0) discountAmount = Math.min(discountAmount, maxValue);
    return Math.max(0, Math.min(discountAmount, lineBase));
  };

  const getItemVatRate = (item: BasketItem): number => {
    const taxes = Array.isArray(item?.taxes) ? item.taxes : [];
    const rate = taxes.reduce((sum, t) => {
      const r = toRateNumber(
        t?.rate ?? t?.taxRate ?? t?.percentage ?? t?.percent ?? t?.amount ??
          t?.vatPercentage ?? t?.value ?? t?.tax?.rate ?? t?.tax?.amount,
      );
      return sum + Math.max(0, r);
    }, 0);
    return rate > 0 ? rate : 0;
  };

  const lineBreakdown = basketItems.map((item) => {
    const lineBase = Number(item.price || 0) * Number(item.qty || 0);
    const lineDiscount = getItemDiscountAmount(item);
    const vatRate = getItemVatRate(item);
    const taxableBase = Math.max(0, lineBase - lineDiscount);
    const lineVat = taxableBase * (vatRate / 100);
    return { ...item, lineDiscount, lineVat, vatRate };
  });

  const totalLineDiscount = lineBreakdown.reduce((s, i) => s + i.lineDiscount, 0);
  const totalLineVat = lineBreakdown.reduce((s, i) => s + i.lineVat, 0);
  const totalDiscount = totalLineDiscount;

  const businessDetails = (() => {
    try { return JSON.parse(localStorage.getItem("businessDetails") || "{}"); } catch { return {}; }
  })();
  const bizData = businessDetails?.data ?? businessDetails;
  const logoUrl = bizData?.imagePath ?? null;
  const bizPhone = bizData?.phoneNumber ?? null;
  const bizCurrency = bizData?.currency?.isO_Code ?? "BDT";
  const bizAddress = (() => {
    const a = bizData?.addressDetail;
    if (!a) return "";
    return [a.addressLine1, a.addressLine2, a.city, a.district, a.division, a.country]
      .map((p) => (p && typeof p === "object" ? p.name : p))
      .filter((p) => p && p !== "string" && p !== "null")
      .join(", ");
  })();
  const outlet = (() => {
    try { return JSON.parse(localStorage.getItem("outlet") || "{}"); } catch { return {}; }
  })();
  const counterInfo = JSON.parse(localStorage.getItem("counterCheckedInInfo") || "{}");
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const bizFacebook =
    bizData?.facebookPage ?? bizData?.facebookPageUrl ?? bizData?.fbPage ??
    bizData?.socialMedia ?? bizData?.website ?? bizData?.websiteUrl ??
    outlet?.facebookPage ?? outlet?.website ?? null;

  const effectiveDate = receiptData?.saleDate ? new Date(receiptData.saleDate) : new Date();
  const storeName = "Ghorer Bazar";
  const orderNo = receiptData?.receiptNo ?? "";
  const creditMemoNo = receiptData?.creditMemoNumber ?? "";
  const dateShort = effectiveDate.toLocaleDateString("en-GB", {
    day: "2-digit", month: "2-digit", year: "2-digit",
  });
  const timeStr = effectiveDate.toLocaleTimeString("en-US", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
  const cashier = receiptData?.cashier || userInfo?.name || userInfo?.email || "Cashier";
  const customerName = receiptData?.customerName || "Walk In Customer";
  const customerCode = receiptData?.customerCode ?? null;
  const binNumber = receiptData?.binNumber ?? null;
  const outletName = counterInfo?.outletName ?? "";
  const storeAddress = bizAddress || outlet?.address || outlet?.location || "";
  const storePhone = bizPhone ?? outlet?.phone ?? outlet?.phoneNumber ?? outlet?.contactNumber ?? null;
  const totalQty = basketItems.reduce((s, i) => s + Number(i.qty || 0), 0);
  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const netPayable = subtotal - totalDiscount + totalLineVat;
  const netDue = Math.round(netPayable);
  const rounding = netDue - netPayable;
  const showNetPayable = totalDiscount > 0 || totalLineVat > 0;
  const showRounding = Math.abs(rounding) >= 0.005;

  const BLK = "color:#000";

  const buildReceiptHtml = () => {
    const itemsHtml = lineBreakdown
      .map(
        (item, idx) => `
        <tr>
          <td style="padding:3px 2px 3px 0;vertical-align:top;font-size:12px;font-weight:bold;${BLK}">${idx + 1}</td>
          <td style="padding:3px 2px;vertical-align:top;${BLK}">
            <div style="font-size:12px;${BLK}">${item.name}</div>
            ${item.code ? `<div style="font-size:11px;${BLK}">SKU: ${item.code}</div>` : ""}
          </td>
          <td style="text-align:right;white-space:nowrap;padding:3px 2px;vertical-align:top;font-size:12px;${BLK}">${fmt(item.price)}</td>
          <td style="text-align:center;padding:3px 2px;vertical-align:top;font-size:12px;${BLK}">${item.qty}</td>
          <td style="text-align:right;white-space:nowrap;padding:3px 0 3px 2px;vertical-align:top;font-size:12px;${BLK}">${fmt(item.total)}</td>
        </tr>`,
      )
      .join("");

    const watermarkHtml = isRefund
      ? `<div style="position:absolute;top:0;left:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;pointer-events:none;overflow:hidden;z-index:10">
          <div style="font-size:56px;font-weight:900;color:rgba(0,0,0,0.55);transform:rotate(-45deg);white-space:nowrap;letter-spacing:4px;-webkit-print-color-adjust:exact;print-color-adjust:exact">REFUNDED</div>
        </div>`
      : "";

    return `
      <div style="position:relative;font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px;line-height:1.5;max-width:300px;padding:0 8px;${BLK}">
        ${watermarkHtml}

        <div style="text-align:center;margin:0;padding:0">
          <div style="display:flex;align-items:center;justify-content:center">
            <img src="${logoUrl ?? `${window.location.origin}/ghorer-bazar-favicon.jpg`}" alt="Ghorer Bazar" style="max-width:100%;max-height:80px;width:auto;height:auto;object-fit:contain;display:block" />
          </div>
          ${!isRefund ? `<div style="font-size:13px;font-weight:bold;margin:6px 0 4px;${BLK}">Credit Memo</div>` : ""}
          ${binNumber ? `<div style="font-size:10px;margin-top:2px;${BLK}">BIN No: ${binNumber}</div>` : ""}
        </div>
        ${outletName ? `<div style="text-align:center;font-size:11px;font-weight:bold;color:#000;margin:2px 0;">Outlet Name: ${outletName}</div><div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>` : ""}

        <table style="width:100%;border-collapse:collapse;font-size:10px;${BLK}">
          <tr>
            <td style="${BLK}">User: ${cashier}</td>
            <td style="text-align:right;white-space:nowrap;${BLK}">Ref. No.: ${orderNo}</td>
          </tr>
          <tr>
            <td style="${BLK}">Date: ${dateShort}</td>
            <td style="text-align:right;${BLK}">${timeStr}</td>
          </tr>
        </table>
        <div style="font-size:10px;${BLK}">Total number of Product: ${lineBreakdown.length}</div>
        <div style="font-size:10px;${BLK}">Total quantity of Product: ${totalQty}</div>
        <div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>

        <table style="width:100%;border-collapse:collapse;table-layout:fixed;${BLK}">
          <colgroup>
            <col style="width:16px"/>
            <col/>
            <col style="width:62px"/>
            <col style="width:22px"/>
            <col style="width:64px"/>
          </colgroup>
          <thead>
            <tr style="border-bottom:1px dashed #ccc">
              <th style="text-align:left;padding:3px 2px 3px 0;font-size:10px;font-weight:bold;${BLK}">Sl.</th>
              <th style="text-align:left;padding:3px 2px;font-size:10px;font-weight:bold;${BLK}">Product</th>
              <th style="text-align:right;padding:3px 2px;font-size:10px;font-weight:bold;${BLK}">Price</th>
              <th style="text-align:center;padding:3px 2px;font-size:10px;font-weight:bold;${BLK}">Qty</th>
              <th style="text-align:right;padding:3px 0 3px 2px;font-size:10px;font-weight:bold;${BLK}">Amount</th>
            </tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
        </table>
        <div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr><td style="padding:2px 0;${BLK}">Sub Total</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(subtotal)}</td></tr>
          <tr><td style="padding:2px 0;${BLK}">(-) Discount</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(totalDiscount)}</td></tr>
          <tr><td style="padding:2px 0;${BLK}">(+) VAT</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(totalLineVat)}</td></tr>
          ${showNetPayable ? `<tr><td style="padding:2px 0;${BLK}">Net Payable</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(netPayable)}</td></tr>` : ""}
          ${showRounding ? `<tr><td style="padding:2px 0;${BLK}">Rounding (+/-)</td><td style="text-align:right;padding:2px 0;${BLK}">${bizCurrency} ${fmt(rounding)}</td></tr>` : ""}
        </table>
        <div style="border-top:1px solid #000;border-bottom:2px solid #000;margin:4px 0;padding:3px 0">
          <table style="width:100%;border-collapse:collapse;${BLK}">
            <tr>
              <td style="font-weight:bold;font-size:13px;${BLK}">REFUNDED AMOUNT</td>
              <td style="text-align:right;font-weight:bold;font-size:13px;${BLK}">${bizCurrency} ${fmt(netDue)}</td>
            </tr>
          </table>
        </div>

        <div style="font-size:10px;${BLK}">Customer: ${customerName}</div>
        <div style="font-size:10px;${BLK}">Customer Code: ${customerCode ?? "N/A"}</div>
        <div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>

        <div style="font-weight:bold;font-size:10px;margin-bottom:2px;${BLK}">Terms &amp; Conditions:</div>
        <div style="font-size:9px;${BLK}">1. Products exchangeable within 15 days of purchase.</div>
        <div style="font-size:9px;${BLK}">2. Exchanged products cannot be re-exchanged.</div>
        <div style="font-size:9px;${BLK}">3. No refund in any form is allowed.</div>
        <div style="border-bottom:1px dashed #ccc;margin:4px 0"></div>

        <div style="text-align:center;margin:8px 0 2px">
          <div style="font-size:9px;margin-bottom:4px;${BLK}">Scan for ${isRefund ? "Refund" : "Exchange"} / Tracking</div>
          <canvas class="barcode-receipt"></canvas>
          <div style="font-size:9px;margin-top:2px;${BLK}">${isRefund ? orderNo : creditMemoNo}</div>
        </div>
        <div style="border-bottom:2px solid #000;margin:4px 0"></div>

        <div style="text-align:center;font-size:9px;margin-top:4px;line-height:1.7;${BLK}">
          ${storeAddress ? `<div style="${BLK}">${storeAddress}</div>` : ""}
          ${storePhone ? `<div style="${BLK}">Tel: ${storePhone}</div>` : ""}
          ${bizFacebook ? `<div style="${BLK}">${bizFacebook}</div>` : ""}
          <div style="margin-top:3px;${BLK}">
            <div style="font-size:11px;${BLK}">Thank you for shopping with ${storeName}!</div>
            <div style="font-size:10px;font-weight:bold;${BLK}">Customer Copy</div>
          </div>
        </div>
        <div style="text-align:right;font-size:9px;margin-top:6px;${BLK}">Powered by biznify.net</div>
      </div>`;
  };

  const handlePrint = async () => {
    if (isSunmiDevice()) {
      if (!receiptPreviewRef.current) return;
      if (!window.SunmiPrinter?.isConnected()) {
        toast.error("Printer not ready. Please check the printer and try again.");
        return;
      }
      setIsPrinting(true);
      try {
        await printOnSunmi(receiptPreviewRef.current, 1);
        onClose();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Print failed");
      }
      setIsPrinting(false);
      return;
    }

    setIsPrinting(true);
    const receiptHtml = buildReceiptHtml();
    const barcodeValue = (isRefund ? orderNo : creditMemoNo) || '0000000000';

    const html = `<!DOCTYPE html><html><head>
      <title>${isRefund ? "Refund" : "Credit Memo"} - ${orderNo || creditMemoNo}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;color:#000}
        html,body{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        @media print{
          @page{margin:0;size:80mm auto}
          body{margin:0}
          *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
        }
      </style>
    </head>
    <body>${receiptHtml}</body>
    <script src="https://unpkg.com/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></` + `script>
    <script>
      window.onload = function() {
        if(typeof JsBarcode !== 'undefined') {
          document.querySelectorAll('canvas.barcode-receipt').forEach(function(el) {
            try { JsBarcode(el, '${barcodeValue}', {format:'CODE128',width:2,height:55,displayValue:false,margin:0,lineColor:'#000',background:'#fff'}); } catch(e){}
          });
        }
        setTimeout(function(){ window.print(); window.close(); }, 500);
      };
    </` + `script>
    </html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    setTimeout(() => {
      URL.revokeObjectURL(url);
      setIsPrinting(false);
      onClose();
    }, 2000);
    if (!win) setIsPrinting(false);
  };

  const blk: React.CSSProperties = { color: "#000" };

  const renderReceiptPreview = () => (
    <div style={{ position: "relative" }}>
      {/* Watermark — refund only */}
      {isRefund && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            overflow: "hidden",
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontSize: "48px",
              fontWeight: 900,
              color: "rgba(0,0,0,0.55)",
              transform: "rotate(-45deg)",
              whiteSpace: "nowrap",
              userSelect: "none",
              letterSpacing: "4px",
            }}
          >
            REFUNDED
          </div>
        </div>
      )}

      {/* Receipt content */}
      <div
        style={{
          fontFamily: "'Arial','Helvetica Neue',sans-serif",
          fontSize: "13px",
          lineHeight: "1.5",
          color: "#000",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", margin: 0, paddingTop: "6px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img
              src={logoUrl ?? "/ghorer-bazar-favicon.jpg"}
              alt="Ghorer Bazar"
              style={{ maxWidth: "100%", maxHeight: "80px", width: "auto", height: "auto", objectFit: "contain", display: "block" }}
            />
          </div>
          {!isRefund && (
            <div style={{ fontSize: "16px", fontWeight: "bold", margin: "6px 0 4px", ...blk }}>
              Credit Memo
            </div>
          )}
          {binNumber && (
            <div style={{ fontSize: "12px", marginTop: "2px", fontWeight: "bold", ...blk }}>
              BIN No: {binNumber}
            </div>
          )}
        </div>

        {/* Outlet name */}
        {outletName && (
          <>
            <div style={{ textAlign: "center", fontSize: "13px", fontWeight: "bold", color: "#000", margin: "2px 0" }}>
              Outlet Name: {outletName}
            </div>
            <div style={{ height: "1px", background: "repeating-linear-gradient(90deg,#ccc 0,#ccc 4px,transparent 4px,transparent 7px)", margin: "4px 0" }} />
          </>
        )}

        {/* User / Ref / Date */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", ...blk }}>
          <tbody>
            <tr>
              <td style={{ ...blk }}>User: {cashier}</td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap", ...blk }}>Ref. No.: {orderNo}</td>
            </tr>
            <tr>
              <td style={{ ...blk }}>Date: {dateShort}&nbsp;&nbsp;&nbsp;{timeStr}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div style={{ fontSize: "12px", ...blk }}>Total number of Product: {lineBreakdown.length}</div>
        <div style={{ fontSize: "12px", ...blk }}>Total quantity of Product: {totalQty}</div>
        <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

        {/* Items table */}
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", ...blk }}>
          <colgroup>
            <col style={{ width: "16px" }} />
            <col />
            <col style={{ width: "62px" }} />
            <col style={{ width: "22px" }} />
            <col style={{ width: "64px" }} />
          </colgroup>
          <thead>
            <tr style={{ borderTop: "1px solid #000", borderBottom: "1px solid #000" }}>
              {(["Sl.", "Product", "Price", "Qty", "Amount"] as const).map((h, i) => (
                <th
                  key={h}
                  style={{
                    textAlign: i === 0 || i === 1 ? "left" : i === 3 ? "center" : "right",
                    padding: "3px 2px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    ...blk,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lineBreakdown.map((item, idx) => (
              <tr key={idx}>
                <td style={{ padding: "3px 2px 3px 0", verticalAlign: "top", fontSize: "12px", fontWeight: "bold", ...blk }}>{idx + 1}</td>
                <td style={{ padding: "3px 2px", verticalAlign: "top", ...blk }}>
                  <div style={{ fontSize: "12px", ...blk }}>{item.name}</div>
                  {item.code && <div style={{ fontSize: "11px", ...blk }}>SKU: {item.code}</div>}
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap", padding: "3px 2px", verticalAlign: "top", fontSize: "12px", ...blk }}>{fmt(item.price)}</td>
                <td style={{ textAlign: "center", padding: "3px 2px", verticalAlign: "top", fontSize: "12px", ...blk }}>{item.qty}</td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap", padding: "3px 0 3px 2px", verticalAlign: "top", fontSize: "12px", ...blk }}>{fmt(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

        {/* Totals */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", ...blk }}>
          <tbody>
            <tr>
              <td style={{ padding: "2px 0", paddingLeft: "35px", fontWeight: "bold", ...blk }}>Sub Total</td>
              <td style={{ textAlign: "right", padding: "2px 0", fontWeight: "bold", ...blk }}>{bizCurrency}&nbsp;&nbsp;{fmt(subtotal)}</td>
            </tr>
            <tr>
              <td style={{ padding: "2px 0", paddingLeft: "35px", ...blk }}>(-) Discount</td>
              <td style={{ textAlign: "right", padding: "2px 0", ...blk }}>{bizCurrency}&nbsp;&nbsp;{fmt(totalDiscount)}</td>
            </tr>
            <tr>
              <td style={{ padding: "2px 0", paddingLeft: "35px", ...blk }}>(+) VAT</td>
              <td style={{ textAlign: "right", padding: "2px 0", ...blk }}>{bizCurrency}&nbsp;&nbsp;{fmt(totalLineVat)}</td>
            </tr>
          </tbody>
        </table>
        {(showNetPayable || showRounding) && (
          <>
            <div style={{ height: "1px", background: "repeating-linear-gradient(90deg,#ccc 0,#ccc 4px,transparent 4px,transparent 7px)", margin: "4px 0" }} />
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", ...blk }}>
              <tbody>
                {showNetPayable && (
                  <tr>
                    <td style={{ padding: "2px 0", paddingLeft: "35px", ...blk }}>Net Payable</td>
                    <td style={{ textAlign: "right", padding: "2px 0", ...blk }}>{bizCurrency}&nbsp;&nbsp;{fmt(netPayable)}</td>
                  </tr>
                )}
                {showRounding && (
                  <tr>
                    <td style={{ padding: "2px 0", paddingLeft: "35px", ...blk }}>Rounding (+/-)</td>
                    <td style={{ textAlign: "right", padding: "2px 0", ...blk }}>{bizCurrency}&nbsp;&nbsp;{fmt(rounding)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
        <div style={{ height: "1px", background: "repeating-linear-gradient(90deg,#ccc 0,#ccc 4px,transparent 4px,transparent 7px)", margin: "4px 0" }} />

        {/* Amount Returned */}
        <table style={{ width: "100%", borderCollapse: "collapse", ...blk }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold", fontSize: "16px", padding: "3px 0", paddingLeft: "35px", ...blk }}>REFUNDED AMOUNT</td>
              <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "16px", padding: "3px 0", ...blk }}>{bizCurrency}&nbsp;&nbsp;{fmt(netDue)}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

        {/* Customer */}
        <div style={{ fontSize: "12px", ...blk }}>Customer: {customerName}</div>
        <div style={{ fontSize: "12px", ...blk }}>Customer Code: {customerCode ?? "N/A"}</div>
        <div style={{ height: "1px", background: "repeating-linear-gradient(90deg,#ccc 0,#ccc 4px,transparent 4px,transparent 7px)", margin: "4px 0" }} />

        {/* Terms */}
        <div style={{ fontWeight: "bold", fontSize: "12px", marginBottom: "2px", ...blk }}>Terms &amp; Conditions:</div>
        <div style={{ fontSize: "11px", ...blk }}>1. Products exchangeable within 15 days of purchase.</div>
        <div style={{ fontSize: "11px", ...blk }}>2. Exchanged products cannot be re-exchanged.</div>
        <div style={{ fontSize: "11px", ...blk }}>3. No refund in any form is allowed.</div>
        <div style={{ height: "1px", background: "repeating-linear-gradient(90deg,#ccc 0,#ccc 4px,transparent 4px,transparent 7px)", margin: "4px 0" }} />

        {/* Barcode */}
        <div style={{ textAlign: "center", margin: "8px 0 2px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ fontSize: "11px", marginBottom: "4px", ...blk }}>Scan for {isRefund ? "Refund" : "Exchange"} / Tracking</div>
          <span data-barcode-img="">
            <Barcode
              value={(isRefund ? orderNo : creditMemoNo) || "0000000000"}
              format="CODE128"
              width={1.5}
              height={55}
              displayValue={false}
              margin={6}
              lineColor="#000"
              background="#fff"
              renderer="canvas"
            />
          </span>
          <div style={{ fontSize: "11px", marginTop: "2px", ...blk }}>{isRefund ? orderNo : creditMemoNo}</div>
        </div>
        <div style={{ height: "2px", background: "#000", margin: "4px 0" }} />

        {/* Footer */}
        <div style={{ textAlign: "center", fontSize: "11px", marginTop: "4px", lineHeight: "1.7", ...blk }}>
          {storeAddress && <div style={blk}>{storeAddress}</div>}
          {storePhone && <div style={blk}>Tel: {storePhone}</div>}
          {bizFacebook && <div style={blk}>{bizFacebook}</div>}
          <div style={{ marginTop: "3px" }}>
            <div style={blk}>Thank you for shopping with {storeName}!</div>
            <div style={{ fontWeight: "bold", ...blk }}>Customer Copy</div>
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: "10px", marginTop: "6px", ...blk }}>Powered by biznify.net</div>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">
              {isRefund ? "Refund" : "Exchange"} Successful
            </h2>
            <p className="text-sm text-gray-300">Print a return receipt or close</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-700 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Receipt Preview */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Receipt Preview</h3>
            <div ref={receiptPreviewRef} className="mx-auto" style={{ width: "302px" }}>
              <div className="bg-white">
                {renderReceiptPreview()}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold cursor-pointer transition-colors"
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className={`px-8 py-3 rounded-lg font-bold transition-colors flex items-center gap-2 ${
              isPrinting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700 text-white cursor-pointer"
            }`}
          >
            {isPrinting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Printing...
              </>
            ) : (
              <>
                <i className="ri-printer-line"></i>
                Print Receipt
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
